// Port of SuiteModel/LinkComments.swift (design-docs/Links — design.md §5): links that importers kept as margin
// comments before the dialect had links — Word's `word-link-N`, Substack's `substack-link-N` and Scrivener's
// `scrivener-weblink-…` ("Link: <address>") and document links `scrivener-link-…` ("Link to “Title” (fragment:<id>)").
// Turning them into links puts each on the words it's anchored to and resolves the comment; one the writer replied to,
// one whose words have gone, or one that can't be a link there (across a line, inside a span) stays as it is.
import type * as A from '@automerge/automerge';
import { currentRange } from '../automerge/anchors.ts';
import type { Comment } from '../contract.ts';
import { readFragment } from '../model/documents.ts';
import { MARKDOWN_KINDS } from '../model/manuscriptSelection.ts';
import { isLinkAddress, linkAddress, linking, type Linked } from '../proseMarkdown.ts';

const PREFIXES = ['word-link-', 'substack-link-', 'scrivener-weblink-', 'scrivener-link-'];

/** `LinkComments.address(of:)`: the address a link comment carries, if it's one of the importers'. */
export function linkCommentAddress(comment: Pick<Comment, 'id' | 'body' | 'status' | 'parentId'>): string | null {
  if (comment.status !== 'open' || comment.parentId !== null || !PREFIXES.some((p) => comment.id.startsWith(p))) return null;
  if (comment.body.startsWith('Link: ')) {
    const address = linkAddress(comment.body.slice('Link: '.length));
    return isLinkAddress(address) ? address : null;
  }
  const match = /\(fragment:([^)\s]+)\)\s*$/u.exec(comment.body);
  return match ? 'fragment:' + match[1] : null;
}

export interface LinkCommentCandidate {
  comment: Comment;
  /** Where its words are now, in Unicode scalars of the current text. */
  range: [number, number];
  address: string;
}

/** `LinkComments.candidates(in:)`: the link comments in a fragment that could become links, with where their words
 *  are now. */
export function linkCommentCandidates(doc: A.Doc<unknown>, comments: readonly Comment[] = readFragment(doc).comments): LinkCommentCandidate[] {
  const repliedTo = new Set(comments.flatMap((c) => (c.parentId !== null ? [c.parentId] : [])));
  return comments.flatMap((comment) => {
    if (repliedTo.has(comment.id)) return [];
    const address = linkCommentAddress(comment);
    if (address === null) return [];
    const range = currentRange(doc, comment);
    return range ? [{ comment, range, address }] : [];
  });
}

/** A fragment's link comments put in its text: the text with the links, and the comments that went in. */
export function linkedComments(text: string, candidates: readonly LinkCommentCandidate[]): { linked: Linked; done: Comment[] } {
  const linked = linking(text, candidates.map((c) => ({ range: c.range, address: c.address })));
  return { linked, done: candidates.filter((_, i) => linked.applied[i]).map((c) => c.comment) };
}

/** `DocumentStore.linkCommentCount(in:)` over fragment documents: how many link comments in the prose ones would become
 *  links. */
export function linkCommentCount(docs: Iterable<A.Doc<unknown>>): number {
  let count = 0;
  for (const doc of docs) {
    const fragment = readFragment(doc);
    if (!MARKDOWN_KINDS.includes(fragment.kind)) continue;
    const candidates = linkCommentCandidates(doc, fragment.comments);
    if (candidates.length) count += linkedComments(fragment.text, candidates).done.length;
  }
  return count;
}
